import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { TreeViewer } from './TreeBrowser'
import { ScopeSummary } from './ScopeSummary'
import { PathScope, searchInNodes, StructureNode } from 'lib/InstallationScope'
import { RefreshIcon } from '../../icons'

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1em;
`

const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
`

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  min-width: 1100px;
  min-height: 600px;

  > * {
    flex: 1 100%;
  }
`

type Props = {
  hierarchyData: StructureNode[]
  initialScopes?: PathScope[]
  onComplete: (scopes: PathScope[]) => void
}

export type ClaimedCommon = {
  name: string
  included: boolean
}

export type ClaimedScopesMap = Map<string, ClaimedCommon[] | undefined>

export const MasterdataAssignment = ({ hierarchyData, initialScopes, onComplete }: Props) => {
  const [pathScopes, setPathScopes] = useState<PathScope[]>(initialScopes ?? [])
  const [match, setMatch] = useState<string[]>([])
  const [searchValue, setSearchValue] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)

  const include = (path: string) => {
    const scopeWhereExcluded = pathScopes.find((scope) => scope.exclude.some((excluded) => excluded === path))
    const existingChild = pathScopes.find((scope) => scope.include.some((included) => included.startsWith(path)))
    const withoutChildScopes = pathScopes.filter(
      (scope) =>
        !scope.include.some((included) => included.startsWith(path)) &&
        !scope.exclude.some((excluded) => excluded === path),
    )

    setPathScopes([
      ...withoutChildScopes,
      scopeWhereExcluded
        ? {
            include: scopeWhereExcluded.include,
            exclude: scopeWhereExcluded.exclude.filter((excluded) => excluded !== path),
          }
        : {
            include: [path],
            exclude: [...(existingChild?.exclude ?? [])],
          },
    ])
  }

  const exclude = (path: string) => {
    const parentScope = pathScopes.find((scope) =>
      scope.include.some((included) => path !== included && path.startsWith(included)),
    )
    const withoutRelevantOrRemovedScope = pathScopes.filter(
      (scope) => !scope.include.some((included) => path.startsWith(included)),
    )
    setPathScopes([
      ...withoutRelevantOrRemovedScope,
      ...(parentScope ? [{ include: parentScope.include, exclude: [...parentScope.exclude, path] }] : []),
    ])
  }

  const clearValues = () => {
    setMatch([])
    setSearchValue('')
  }

  return (
    <RowContainer>
      <Heading>
        <div className="w-full grid grid-cols-2">
          <div className="justify-center inline-flex w-3/4 ml-md">
            <form
              className="flex m-md"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (inputRef.current?.value) {
                  const matchedNodes: string[] = searchInNodes(inputRef.current?.value, hierarchyData, '', [])
                  setMatch(matchedNodes)
                  setSearchValue(inputRef.current?.value)
                }
              }}
            >
              <input className="textfield w-1/2" ref={inputRef} type="text" />
              <button className="btn w-1/2 ml-sm">Search</button>
              <button type="reset" className="ml-sm" onClick={clearValues}>
                <RefreshIcon size="30" />
              </button>
            </form>
          </div>
          <div className="mt-sm">
            <h3 className="mt-md float-left">Define Scope For Common Assortment</h3>
            <button className="btn btn-primary-dark float-right py-sm mr-lg" onClick={() => onComplete(pathScopes)}>
              Save
            </button>
          </div>
        </div>
      </Heading>
      {searchValue && match.length === 0 ? <span>No data found</span> : null}
      <Container>
        <TreeViewer scopes={pathScopes} include={include} exclude={exclude} nodes={hierarchyData} searchHit={match} />
        <ScopeSummary scopes={pathScopes} data={hierarchyData} filters={[]} />
      </Container>
    </RowContainer>
  )
}
